import {Badge} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {
  FinancialProductCalculation,
  FinancialProductOffer,
  FinancialProductPreferenceCoordinates,
} from '@dms/api/sales';
import {
  FinancialProductCalculator,
  FinancialProductCalculatorResult,
} from '@dms/features/financial-products';
import i18n from '@dms/i18n';

import {
  constrainFinancingSelection,
  getDownPaymentAdjustment,
  getFinancingControlLimits,
  isValidBalloonCombination,
  type DisplayedFinancialProductQuote,
  type DownPaymentAdjustment,
} from 'features/financial-products';

export interface FinancingPreferenceEditorProps {
  type: 'STANDARD' | 'BALLOON';
  offer: FinancialProductOffer;
  coordinates: Extract<
    FinancialProductPreferenceCoordinates,
    {productType: 'STANDARD' | 'BALLOON'}
  >;
  displayed: DisplayedFinancialProductQuote;
  vehiclePrice: {amount: number; currency: string};
  isDisabled: boolean;
  onChange: (coordinates: FinancialProductPreferenceCoordinates) => void;
}

export function FinancingPreferenceEditor(props: FinancingPreferenceEditorProps) {
  const [adjustment, setAdjustment] = useState<DownPaymentAdjustment | null>(null);
  const selection = {
    termMonths: props.coordinates.termMonths,
    downPaymentPercentage: props.coordinates.downPaymentPercentage,
    balloonPaymentPercentage:
      props.coordinates.productType === 'BALLOON'
        ? props.coordinates.balloonPaymentPercentage
        : null,
  };
  const limits = getFinancingControlLimits(props.offer, selection);
  const isLocallyUnavailable =
    props.displayed.source === 'LOCAL' && props.displayed.quote.availability === 'UNAVAILABLE';

  return (
    <FinancialProductCalculator
      vehiclePrice={props.vehiclePrice.amount}
      currency={props.vehiclePrice.currency}
      selection={selection}
      availableTerms={limits.terms}
      availableDownPaymentPercentages={limits.downPayment?.ticks ?? []}
      downPaymentBrackets={limits.downPayment?.brackets}
      downPaymentAdjustment={adjustment}
      availableBalloonPaymentPercentages={
        props.type === 'BALLOON' ? limits.balloonPaymentPercentages : []
      }
      downPaymentStep={props.offer.downPaymentStep}
      result={toCalculatorResult(
        props.displayed.source === 'NONE' ? null : props.displayed.quote.calculation
      )}
      // Without a quote (rates withheld or an older backend) the figures stay placeholders.
      emptyResult={
        isLocallyUnavailable ? (
          <VStack spacing={2}>
            <Badge
              value={i18n.t('page.vehicle.financingAndLeasing.outsideMatrix')}
              colorScheme="orange"
              variant="subtle"
            />
            <Text color="tertiary">
              {i18n.t('page.vehicle.financingAndLeasing.outsideMatrixExplanation')}
            </Text>
          </VStack>
        ) : undefined
      }
      badge={
        <Show when={isDefaultSelection(props.offer, props.coordinates)}>
          <Badge
            value={i18n.t('page.vehicle.financingAndLeasing.defaultOption')}
            colorScheme="yellow"
            variant="subtle"
          />
        </Show>
      }
      isDisabled={props.isDisabled}
      onSelectionChange={(changedSelection) => {
        if (isNil(changedSelection.termMonths)) {
          return;
        }
        // Snapped here, so a term change prices one coordinate the term actually offers.
        const constrained = constrainFinancingSelection(props.offer, changedSelection);
        setAdjustment(
          changedSelection.termMonths !== selection.termMonths ||
            changedSelection.balloonPaymentPercentage !== selection.balloonPaymentPercentage
            ? getDownPaymentAdjustment(changedSelection, constrained)
            : null
        );
        const termMonths = changedSelection.termMonths;

        if (props.type === 'STANDARD') {
          props.onChange({
            productType: 'STANDARD',
            termMonths,
            downPaymentPercentage: constrained.downPaymentPercentage,
          });
          return;
        }

        if (
          isNil(constrained.balloonPaymentPercentage) ||
          !props.offer.availableBalloonPaymentPercentages.includes(
            constrained.balloonPaymentPercentage
          ) ||
          !isValidBalloonCombination(
            constrained.downPaymentPercentage,
            constrained.balloonPaymentPercentage
          )
        ) {
          return;
        }

        props.onChange({
          productType: 'BALLOON',
          termMonths,
          downPaymentPercentage: constrained.downPaymentPercentage,
          balloonPaymentPercentage: constrained.balloonPaymentPercentage,
        });
      }}
      data-testid="financingPreference"
    />
  );
}

function toCalculatorResult(
  calculation: FinancialProductCalculation | null
): FinancialProductCalculatorResult | null {
  if (isNil(calculation)) {
    return null;
  }

  const money = (value: {amount: string; currency: string} | null) =>
    isNil(value) ? null : Number(value.amount);

  return {
    monthlyPayment: Number(calculation.monthlyPayment.amount),
    nominalRate: calculation.nominalRate,
    apr: calculation.apr,
    netCredit: money(calculation.netCredit),
    grossCredit: money(calculation.grossCredit),
    totalInterest: money(calculation.totalInterest),
    totalPayable: money(calculation.totalPayable),
    downPaymentAmount: money(calculation.downPaymentAmount),
    balloonPaymentAmount: money(calculation.balloonPaymentAmount),
  };
}

/**
 * The vehicle's configured default, the meaning the vehicle tab gives the badge. Not the displayed
 * calculation: that prices the draft itself, so it would always match.
 */
function isDefaultSelection(
  offer: FinancialProductOffer,
  coordinates: FinancingPreferenceEditorProps['coordinates']
): boolean {
  const defaultSelection = offer.defaultSelection;

  return (
    !isNil(defaultSelection) &&
    defaultSelection.termMonths === coordinates.termMonths &&
    defaultSelection.downPaymentPercentage === coordinates.downPaymentPercentage &&
    defaultSelection.balloonPaymentPercentage ===
      (coordinates.productType === 'BALLOON' ? coordinates.balloonPaymentPercentage : null)
  );
}
